import { Bell, BellOff, LogOut, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, NavLink, Outlet } from "react-router-dom";

import { UserAvatar } from "@/components/user-avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { t } from "@/i18n/pl";
import { disablePush, enablePush, getSubscription, pushSupported } from "@/lib/push";
import { cn } from "@/lib/utils";

/** Bell toggle: subscribe this device to chat / ride-change / request pushes. */
function PushBell() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void getSubscription().then((subscription) => setEnabled(subscription !== null));
  }, []);

  if (!pushSupported()) return null;

  async function toggle() {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success(t.push.disabled);
      } else if (Notification.permission === "denied") {
        toast.error(t.push.denied);
      } else {
        const ok = await enablePush();
        setEnabled(ok);
        if (ok) toast.success(t.push.enabled);
      }
    } catch {
      toast.error(t.common.genericError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
      aria-label={enabled ? t.push.disable : t.push.enable}
      aria-pressed={enabled}
      disabled={busy}
      onClick={() => void toggle()}
    >
      {enabled ? <Bell /> : <BellOff className="opacity-60" />}
    </Button>
  );
}

export function AppShell() {
  const { user, signOut } = useAuth();

  return (
    <div className="flex min-h-full flex-col">
      {/* Navy header carries the white+gold brand logo, like zmskiba.pl.
          Sticky so the brand + sign-out stay reachable on long mobile pages. */}
      <header className="bg-sidebar text-sidebar-foreground sticky top-0 z-40 shadow-md">
        <div className="mx-auto flex h-14 w-full max-w-3xl items-center gap-3 px-4 sm:h-16">
          <Link to="/" className="flex items-center gap-3 font-semibold" aria-label={t.nav.home}>
            <img src="/logo.png" alt="" className="h-8 w-auto sm:h-9" />
            <span className="hidden sm:inline">{t.appName}</span>
          </Link>

          {user?.is_admin && (
            <nav aria-label={t.nav.admin} className="ml-1">
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                  )
                }
              >
                <ShieldCheck className="size-4" aria-hidden />
                <span className="hidden sm:inline">{t.nav.admin}</span>
              </NavLink>
            </nav>
          )}

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5">
            <PushBell />
            {user && (
              <NavLink
                to="/ustawienia"
                aria-label={t.nav.settings}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors",
                    isActive ? "bg-sidebar-accent" : "hover:bg-sidebar-accent",
                  )
                }
              >
                <UserAvatar user={user} className="size-8" fallbackClassName="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-bold" />
                <span className="hidden text-sm md:inline">{user.full_name}</span>
              </NavLink>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={() => void signOut()}
              aria-label={t.auth.signOut}
            >
              <LogOut />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-6 pb-12 sm:py-8">
        <Outlet />
      </main>

      <footer className="text-muted-foreground mx-auto flex w-full max-w-3xl flex-col items-center gap-1 px-4 pb-6 text-center text-xs">
        <p>
          <Link to="/prywatnosc" className="underline underline-offset-2">
            {t.footer.privacy}
          </Link>
          {" · "}
          <a
            href={t.privacy.corporatePolicyUrl}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2"
          >
            {t.footer.corporatePolicy}
          </a>
        </p>
        {/* Which build is this? Saves a round of "did the update land?". */}
        <p className="text-muted-foreground/60 tabular-nums">
          {t.appName} v{__APP_VERSION__} · {__COMMIT_SHA__}
        </p>
      </footer>
    </div>
  );
}
